import type { ArenaConfig, BotRosterConfig } from "../config/gameplay-config";
import type { BotEntity, BotGameplayTraits, BotPosition } from "./bot-roster";
import type { FoodEntity } from "./food-population";
import {
  createPlayableArenaBounds,
  hasArenaBoundaryContact,
} from "./arena-boundary";
import type { RandomSource } from "./seeded-random";
import { headingToward } from "./player-steering";
import { stepSnakeLocomotion } from "./snake-locomotion";

export type BotBehavior = "flee" | "hunt" | "seek-food" | "wander";
export type BotRecoveryReason = "boundary" | "stalled";

export interface PerceivedSnake {
  readonly id: string;
  readonly level: number;
  readonly position: BotPosition;
}

export interface BotTarget {
  readonly foodId: string | null;
  readonly kind: BotBehavior;
  readonly position: BotPosition;
  readonly selectedAtMs: number;
  readonly snakeId: string | null;
}

export interface BotRecoveryState {
  readonly reason: BotRecoveryReason | null;
  readonly untilMs: number;
}

interface BotProgressState {
  readonly objectiveDistance: number;
  readonly targetIdentity: string;
  readonly updatedAtMs: number;
}

export interface BotBehaviorState {
  readonly behavior: BotBehavior;
  readonly directionRadians: number;
  readonly position: BotPosition;
  readonly progress: BotProgressState;
  readonly recovery: BotRecoveryState;
  readonly target: BotTarget;
}

export interface BotBehaviorOptions {
  readonly arena: ArenaConfig;
  readonly bot: BotEntity;
  readonly config: BotRosterConfig;
  readonly foods: readonly FoodEntity[];
  readonly random: RandomSource;
  readonly simulationTimeMs: number;
  readonly snakes?: readonly PerceivedSnake[];
}

export interface BotBehaviorStepOptions extends BotBehaviorOptions {
  readonly state: BotBehaviorState;
  readonly stepMilliseconds: number;
}

const squaredDistance = (left: BotPosition, right: BotPosition): number => {
  const x = left.x - right.x;
  const y = left.y - right.y;
  return x * x + y * y;
};

const distance = (left: BotPosition, right: BotPosition): number =>
  Math.sqrt(squaredDistance(left, right));

const chooseNearestFood = (
  position: BotPosition,
  traits: BotGameplayTraits,
  foods: readonly FoodEntity[],
): FoodEntity | null => {
  const maximumSquaredDistance = traits.perceptionRadius ** 2;
  let nearest: FoodEntity | null = null;
  let nearestSquaredDistance = Number.POSITIVE_INFINITY;
  for (const food of foods) {
    const candidateSquaredDistance = squaredDistance(position, food.position);
    if (candidateSquaredDistance > maximumSquaredDistance) {
      continue;
    }
    if (
      candidateSquaredDistance < nearestSquaredDistance ||
      (candidateSquaredDistance === nearestSquaredDistance &&
        nearest !== null &&
        food.id < nearest.id)
    ) {
      nearest = food;
      nearestSquaredDistance = candidateSquaredDistance;
    }
  }
  return nearest;
};

const chooseNearestSnake = (
  options: BotBehaviorOptions,
  eligible: (snake: PerceivedSnake) => boolean,
): PerceivedSnake | null => {
  const maximumSquaredDistance = options.bot.traits.perceptionRadius ** 2;
  let nearest: PerceivedSnake | null = null;
  let nearestSquaredDistance = Number.POSITIVE_INFINITY;
  for (const snake of options.snakes ?? []) {
    if (snake.id === options.bot.id || !eligible(snake)) {
      continue;
    }
    const candidateSquaredDistance = squaredDistance(
      options.bot.position,
      snake.position,
    );
    if (candidateSquaredDistance > maximumSquaredDistance) {
      continue;
    }
    if (
      candidateSquaredDistance < nearestSquaredDistance ||
      (candidateSquaredDistance === nearestSquaredDistance &&
        nearest !== null &&
        snake.id < nearest.id)
    ) {
      nearest = snake;
      nearestSquaredDistance = candidateSquaredDistance;
    }
  }
  return nearest;
};

const createFoodTarget = (
  food: FoodEntity,
  simulationTimeMs: number,
): BotTarget => ({
  foodId: food.id,
  kind: "seek-food",
  position: { ...food.position },
  selectedAtMs: simulationTimeMs,
  snakeId: null,
});

const createSnakeTarget = (
  kind: "flee" | "hunt",
  snake: PerceivedSnake,
  simulationTimeMs: number,
): BotTarget => ({
  foodId: null,
  kind,
  position: { ...snake.position },
  selectedAtMs: simulationTimeMs,
  snakeId: snake.id,
});

const createWanderTarget = (
  bot: BotEntity,
  arena: ArenaConfig,
  random: RandomSource,
  simulationTimeMs: number,
): BotTarget => {
  const bounds = createPlayableArenaBounds(arena, bot.collision.radius);
  return {
    foodId: null,
    kind: "wander",
    position: {
      x:
        bounds.minimumX +
        random.nextUnit() * (bounds.maximumX - bounds.minimumX),
      y:
        bounds.minimumY +
        random.nextUnit() * (bounds.maximumY - bounds.minimumY),
    },
    selectedAtMs: simulationTimeMs,
    snakeId: null,
  };
};

const choosePriorityTarget = (
  options: BotBehaviorOptions,
): BotTarget | null => {
  const threat = chooseNearestSnake(
    options,
    (snake) =>
      snake.level - options.bot.traits.level >= options.config.fleeLevelGap,
  );
  if (threat !== null) {
    return createSnakeTarget("flee", threat, options.simulationTimeMs);
  }
  const prey = chooseNearestSnake(
    options,
    (snake) =>
      options.bot.traits.level - snake.level >= options.config.huntLevelGap,
  );
  if (prey !== null) {
    return createSnakeTarget("hunt", prey, options.simulationTimeMs);
  }
  const food =
    options.bot.traits.targetPreference === "food"
      ? chooseNearestFood(
          options.bot.position,
          options.bot.traits,
          options.foods,
        )
      : null;
  return food === null
    ? null
    : createFoodTarget(food, options.simulationTimeMs);
};

const createPreferredTarget = (options: BotBehaviorOptions): BotTarget =>
  choosePriorityTarget(options) ??
  createWanderTarget(
    options.bot,
    options.arena,
    options.random,
    options.simulationTimeMs,
  );

const behaviorPriority = (behavior: BotBehavior): number => {
  switch (behavior) {
    case "flee":
      return 5;
    case "hunt":
      return 3;
    case "seek-food":
      return 2;
    case "wander":
      return 1;
  }
};

const targetIdentity = (target: BotTarget): string =>
  `${target.kind}:${target.snakeId ?? target.foodId ?? "point"}`;

const objectiveDistance = (
  position: BotPosition,
  target: BotTarget,
): number => {
  const targetDistance = distance(position, target.position);
  return target.kind === "flee" ? -targetDistance : targetDistance;
};

const currentSnakeTarget = (
  options: BotBehaviorStepOptions,
): PerceivedSnake | null =>
  (options.snakes ?? []).find(
    (snake) => snake.id === options.state.target.snakeId,
  ) ?? null;

const retainsSnakeTarget = (
  options: BotBehaviorStepOptions,
  snake: PerceivedSnake,
): boolean => {
  const gap =
    options.state.target.kind === "flee"
      ? snake.level - options.bot.traits.level
      : options.bot.traits.level - snake.level;
  const entryGap =
    options.state.target.kind === "flee"
      ? options.config.fleeLevelGap
      : options.config.huntLevelGap;
  const retainedGap = entryGap - options.config.targetLevelHysteresis;
  const retainedRange =
    options.bot.traits.perceptionRadius + options.config.targetRangeHysteresis;
  return (
    gap >= retainedGap &&
    squaredDistance(options.state.position, snake.position) <=
      retainedRange ** 2
  );
};

const updateSnakeTarget = (
  options: BotBehaviorStepOptions,
  preferred: BotTarget | null,
  committed: boolean,
): BotTarget => {
  const currentSnake = currentSnakeTarget(options);
  if (currentSnake === null || !retainsSnakeTarget(options, currentSnake)) {
    return (
      preferred ??
      createWanderTarget(
        options.bot,
        options.arena,
        options.random,
        options.simulationTimeMs,
      )
    );
  }
  const current = {
    ...options.state.target,
    position: { ...currentSnake.position },
  };
  if (
    committed ||
    preferred?.kind !== current.kind ||
    preferred.snakeId === current.snakeId
  ) {
    return current;
  }
  const currentDistance = distance(options.state.position, current.position);
  const preferredDistance = distance(
    options.state.position,
    preferred.position,
  );
  return currentDistance - preferredDistance >=
    options.config.targetSwitchMinimumAdvantage
    ? preferred
    : current;
};

const updateFoodTarget = (
  options: BotBehaviorStepOptions,
  preferred: BotTarget | null,
  committed: boolean,
): BotTarget => {
  const currentFood = options.foods.find(
    (food) => food.id === options.state.target.foodId,
  );
  const currentInRange =
    currentFood !== undefined &&
    squaredDistance(options.state.position, currentFood.position) <=
      options.bot.traits.perceptionRadius ** 2;
  if (!currentInRange) {
    return (
      preferred ??
      createWanderTarget(
        options.bot,
        options.arena,
        options.random,
        options.simulationTimeMs,
      )
    );
  }
  const current = {
    ...options.state.target,
    position: { ...currentFood.position },
  };
  if (
    committed ||
    preferred?.kind !== "seek-food" ||
    preferred.foodId === current.foodId
  ) {
    return current;
  }
  const currentDistance = distance(options.state.position, current.position);
  const preferredDistance = distance(
    options.state.position,
    preferred.position,
  );
  return currentDistance - preferredDistance >=
    options.config.targetSwitchMinimumAdvantage
    ? preferred
    : current;
};

const updateTarget = (options: BotBehaviorStepOptions): BotTarget => {
  if (
    options.state.recovery.reason !== null &&
    options.simulationTimeMs < options.state.recovery.untilMs
  ) {
    return options.state.target;
  }
  const preferred = choosePriorityTarget(options);
  if (
    preferred !== null &&
    behaviorPriority(preferred.kind) >= behaviorPriority("hunt") &&
    behaviorPriority(preferred.kind) > behaviorPriority(options.state.behavior)
  ) {
    return preferred;
  }
  const committed =
    options.simulationTimeMs - options.state.target.selectedAtMs <
    options.config.retargetCooldownMs;
  if (
    options.state.target.kind === "flee" ||
    options.state.target.kind === "hunt"
  ) {
    return updateSnakeTarget(options, preferred, committed);
  }
  if (options.state.target.kind === "seek-food") {
    return updateFoodTarget(options, preferred, committed);
  }
  const reached =
    squaredDistance(options.state.position, options.state.target.position) <=
    options.config.wanderTargetReachDistance ** 2;
  if (reached) {
    return createWanderTarget(
      options.bot,
      options.arena,
      options.random,
      options.simulationTimeMs,
    );
  }
  return preferred !== null && !committed ? preferred : options.state.target;
};

const headingForTarget = (
  position: BotPosition,
  target: BotTarget,
  fallback: number,
): number => {
  const toward = headingToward(position, target.position, fallback);
  return target.kind === "flee" ? toward + Math.PI : toward;
};

const recoveryHeading = (
  position: BotPosition,
  arena: ArenaConfig,
  fallback: number,
): number =>
  headingToward(
    position,
    { x: arena.width / 2, y: arena.height / 2 },
    fallback,
  );

const initialProgress = (
  position: BotPosition,
  target: BotTarget,
  simulationTimeMs: number,
): BotProgressState => ({
  objectiveDistance: objectiveDistance(position, target),
  targetIdentity: targetIdentity(target),
  updatedAtMs: simulationTimeMs,
});

export const createBotBehaviorState = (
  options: BotBehaviorOptions,
): BotBehaviorState => {
  const target = createPreferredTarget(options);
  return {
    behavior: target.kind,
    directionRadians: headingForTarget(options.bot.position, target, 0),
    position: { ...options.bot.position },
    progress: initialProgress(
      options.bot.position,
      target,
      options.simulationTimeMs,
    ),
    recovery: { reason: null, untilMs: options.simulationTimeMs },
    target,
  };
};

export const relocateBotBehaviorState = (
  state: BotBehaviorState,
  position: BotPosition,
  simulationTimeMs: number,
): BotBehaviorState => ({
  ...state,
  position: { ...position },
  progress: initialProgress(position, state.target, simulationTimeMs),
  recovery: { reason: null, untilMs: simulationTimeMs },
});

export const stepBotBehavior = (
  options: BotBehaviorStepOptions,
): BotBehaviorState => {
  const target = updateTarget(options);
  const identity = targetIdentity(target);
  const targetChanged = identity !== options.state.progress.targetIdentity;
  const recoveryStillActive =
    options.state.recovery.reason !== null &&
    options.simulationTimeMs < options.state.recovery.untilMs;
  const stalled =
    !targetChanged &&
    !recoveryStillActive &&
    options.simulationTimeMs - options.state.progress.updatedAtMs >=
      options.config.stuckDetectionMs;
  let recovery: BotRecoveryState = recoveryStillActive
    ? options.state.recovery
    : {
        reason: stalled ? "stalled" : null,
        untilMs: stalled
          ? options.simulationTimeMs + options.config.recoveryDurationMs
          : options.simulationTimeMs,
      };
  const targetHeading =
    recovery.reason === null
      ? headingForTarget(
          options.state.position,
          target,
          options.state.directionRadians,
        )
      : recoveryHeading(
          options.state.position,
          options.arena,
          options.state.directionRadians,
        );
  const movement = stepSnakeLocomotion(
    {
      collision: options.bot.collision,
      directionRadians: options.state.directionRadians,
      position: options.state.position,
      speed: options.config.movementSpeed,
    },
    targetHeading,
    options.config.turnRateRadiansPerSecond,
    options.stepMilliseconds,
    options.arena,
  );
  const boundaryContact = hasArenaBoundaryContact(movement.contact);
  if (recovery.reason === null && boundaryContact) {
    recovery = {
      reason: "boundary",
      untilMs: options.simulationTimeMs + options.config.recoveryDurationMs,
    };
  }
  const measuredObjectiveDistance = objectiveDistance(
    movement.snake.position,
    target,
  );
  const madeProgress =
    measuredObjectiveDistance <=
    options.state.progress.objectiveDistance -
      options.config.stuckDistanceEpsilon;
  const resetProgress =
    targetChanged || madeProgress || stalled || recovery.reason === "boundary";
  return {
    behavior: target.kind,
    directionRadians: movement.snake.directionRadians,
    position: movement.snake.position,
    progress: resetProgress
      ? initialProgress(
          movement.snake.position,
          target,
          options.simulationTimeMs,
        )
      : options.state.progress,
    recovery,
    target,
  };
};
